<script setup lang="ts">
const props = defineProps<{
  description: string
  icon: string
  loadingText?: string
  reset?: () => void
  title: string
}>()

const { t } = useI18n()
const router = useRouter()
const { open: openAbout } = useAboutModal()
const { open: openHelp } = useGameHelpModal()
const toast = useToast()

const { data: footerContent } = await useAsyncData('component-footer', () =>
  queryCollection('componentFooter').first(),
)

const footerCreditParts = computed(() => {
  const line = footerContent.value?.credit.line ?? ''
  const [ before, after = '' ] = line.split('{kaweahTech}')
  return { after, before }
})

async function handleShare() {
  if (!import.meta.client) {
    return
  }
  const url = window.location.href
  if (navigator.share) {
    try {
      await navigator.share({ title: document.title, url })
      return
    } catch {
      // user cancelled or browser blocked — fall through to clipboard fallback
    }
  }
  try {
    await navigator.clipboard.writeText(url)
    toast.add({
      color: 'success',
      description: t('setupMenu.linkCopied'),
      duration: 2000,
      icon: 'mdi:check-circle',
    })
  } catch {
    // Last resort: silently swallow.
  }
}

const menuItems = computed(() => {
  // Order intentionally pairs Help directly above About so the two
  // meta-info items sit together at the bottom of the menu, with
  // game-specific actions (share, reset) up top.
  const items: Array<{
    icon: string
    label: string
    onSelect: () => void
  }> = [ { icon: 'mdi:share-variant-outline', label: t('setupMenu.shareGame'), onSelect: handleShare } ]
  if (props.reset) {
    items.push({
      icon: 'mdi:refresh',
      label: t('setupMenu.resetSettings'),
      onSelect: () => props.reset?.(),
    })
  }
  items.push({
    icon: 'mdi:help-circle-outline',
    label: t('setupMenu.help'),
    onSelect: () => openHelp('setup_menu'),
  })
  items.push({
    icon: 'mdi:information-outline',
    label: t('setupMenu.about'),
    onSelect: () => openAbout('setup_menu'),
  })
  items.push({
    icon: 'mdi:arrow-left',
    label: t('setupMenu.backToLibrary'),
    onSelect: () => router.push('/games'),
  })
  return items
})
</script>

<template>
  <UCard :ui="{ root: 'shadow-xl ring', header: 'bg-muted/75', body: 'bg-muted/5', footer: 'bg-muted/20' }">
    <!-- Card header: icon + title/description + corner menu button -->
    <template #header>
      <div class="flex items-start gap-4 py-1">
        <UIcon
          class="size-12 shrink-0"
          :name="icon"
        />
        <div class="min-w-0 flex-1">
          <h1 class="text-2xl font-bold leading-tight">
            {{ title }}
          </h1>
          <p class="mt-1 text-sm text-muted">
            {{ description }}
          </p>
        </div>
        <UDropdownMenu
          :items="menuItems"
          :content="{ side: 'bottom', align: 'end', sideOffset: 8 }"
          :modal="false"
          :ui="{ content: 'w-48' }"
        >
          <SfgChromeButton
            square
            icon-left="mdi:dots-vertical"
            :aria-label="t('setupMenu.menuAriaLabel')"
            class="shrink-0"
          />
        </UDropdownMenu>
      </div>
    </template>

    <template #footer>
      <div v-if="footerContent" class="w-full flex flex-col items-center gap-3 text-center">
        <NuxtLink to="/" class="inline-flex items-center gap-3 group">
          <SfgFlame
            :size="32"
            :disk="false"
            body-color="red"
            flame-color="yellow"
            :tilt="1"
          />
          <div class="flex flex-col leading-tight text-left">
            <span class="font-display uppercase text-[12px] tracking-[0.08em] text-default group-hover:text-primary transition-colors">
              {{ footerContent.wordmark.label }}
            </span>
            <span class="font-body text-[10px] text-muted">
              {{ footerContent.wordmark.sublabel }}
            </span>
          </div>
        </NuxtLink>
        <p class="font-body text-[11px] text-muted">
          {{ footerContent.credit.year }} / {{ footerCreditParts.before }}<a
            :href="footerContent.credit.kaweahTechUrl"
            target="_blank"
            rel="noopener"
            class="text-inherit no-underline hover:underline"
          >Kaweah Tech</a>{{ footerCreditParts.after }}
        </p>
      </div>
    </template>

    <!-- Setup form -->
    <ClientOnly>
      <slot/>
      <template #fallback>
        <div class="flex flex-col items-center gap-4 py-16 text-center">
          <UIcon
            class="size-12 text-primary animate-pulse"
            name="mdi:progress-clock"
          />
          <h2 class="text-balance text-2xl font-black tracking-tight">
            {{ loadingText ?? 'Loading…' }}
          </h2>
        </div>
      </template>
    </ClientOnly>
  </UCard>
</template>
